import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { loadAppSettings, saveAppSettings } from './appSettings';
import useDocumentsAdmin from './admin/hooks/useDocumentsAdmin';
import { formatWingitDate } from './dateFormat';
import ClubAppearancePanel from './admin/ClubAppearancePanel';
import { saveClubBranding } from './branding/brandingService';
import DocumentsPanel from './admin/DocumentsPanel';
import AircraftFleetPanel from './admin/AircraftFleetPanel';
import useAircraftAdmin from './admin/hooks/useAircraftAdmin';
import ClubSettingsPanel from './admin/ClubSettingsPanel';
import AnnouncementsPanel from './admin/AnnouncementsPanel';
import AdminPaymentsPanel from './admin/AdminPaymentsPanel';
import StudentTrainingManagement from './StudentTrainingManagement';
import {
  defaultInvoiceSettings,
  normalizeInvoiceSettings,
} from './invoicing/invoiceSettings';
import { membershipAllows } from './membership';
import { useOrganisationSubscriptionTier } from './useOrganisationSubscriptionTier';

function normalizeRole(...candidates) {
  for (const candidate of candidates) {
    const value = String(candidate || '').trim().toLowerCase();
    if (value) return value;
  }
  return '';
}

function collectRoles(...candidates) {
  return candidates
    .map(candidate => String(candidate || '').trim().toLowerCase())
    .filter(Boolean);
}

function isOwnerByEmail(email) {
  const normalized = String(email || '').trim().toLowerCase();
  return normalized === 'owner@demo.wingit.app';
}

export function isAdminPaymentsView(searchParams) {
  return searchParams?.get('section') === 'operations'
    && searchParams?.get('view') === 'payments';
}

export function isStudentTrainingView(searchParams) {
  return searchParams?.get('section') === 'operations'
    && searchParams?.get('view') === 'student-training';
}

export function canAccessAdminPayments(subscriptionTier) {
  return membershipAllows(subscriptionTier, 'payments');
}

function AdminPaymentsFeatureGate({
  user,
  organisationId,
  accessToken,
  onBack,
}) {
  const {
    organisationSubscriptionTier,
    organisationSubscriptionLoading,
  } = useOrganisationSubscriptionTier(user);

  if (organisationSubscriptionLoading) {
    return (
      <div style={{ color: '#b6eaff', padding: '2rem' }}>
        Loading organisation package...
      </div>
    );
  }

  if (!canAccessAdminPayments(organisationSubscriptionTier)) {
    return (
      <Navigate
        to="/upgrade?feature=payments"
        replace
      />
    );
  }

  return (
    <AdminPaymentsPanel
      organisationId={organisationId}
      isAdmin
      accessToken={accessToken}
      onBack={onBack}
    />
  );
}

const emptyAirfieldDraft = {
  name: '',
  icao: '',
  weatherIcao: '',
  weatherSourceName: '',
  notes: '',
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = useSession();
  const user = session?.user;
  const organisationId =
  session?.profile?.organisation_id ||
  session?.user?.organisation_id ||
  '';
  
  const [resolvedRole, setResolvedRole] = useState('');
const [roleLoading, setRoleLoading] = useState(true);

const [clubBaseAirfieldId, setClubBaseAirfieldId] = useState('');
const [airfieldDraft, setAirfieldDraft] = useState(emptyAirfieldDraft);
const [airfieldStatus, setAirfieldStatus] = useState('');
const [airfieldError, setAirfieldError] = useState('');

const [appSettings, setAppSettings] = useState(loadAppSettings(organisationId));
const [logoUploadError, setLogoUploadError] = useState('');
const [clubHeroFiles, setClubHeroFiles] = useState([]);
const [clubLogoFile, setClubLogoFile] = useState(null);
const [clubAppearanceStatus, setClubAppearanceStatus] = useState('');
const [setupStatus, setSetupStatus] = useState('');
const [clubSettingsOpen, setClubSettingsOpen] = useState(false);
const [invoiceSettings, setInvoiceSettings] = useState(
  defaultInvoiceSettings
);
const [invoiceSavedSettings, setInvoiceSavedSettings] =
  useState(defaultInvoiceSettings);
const [invoiceSettingsStatus, setInvoiceSettingsStatus] =
  useState('');
const documentsAdmin = useDocumentsAdmin();
const {
  documents,
  documentsStatus,
  handleDocumentUpload,
  handleDeleteDocument,
  handlePrintDocument,
} = documentsAdmin;
  const [stripeConnectLoading, setStripeConnectLoading] = useState(false);
  const apiBase = String(process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');

  
  const fetchClubBaseAirfield = useCallback(async () => {
    setAirfieldError('');

    if (!organisationId) {
      setClubBaseAirfieldId('');
      setAirfieldDraft(emptyAirfieldDraft);
      return;
    }

    const { data, error } = await supabase
      .from('airfields')
      .select('id, name, icao, weather_icao, weather_source_name, notes, singleton, organisation_id')
      .eq('organisation_id', organisationId)
      .maybeSingle();

    if (error) {
      setAirfieldError('Could not load club base airfield. Check the airfields SQL table exists.');
      return;
    }

    if (!data) {
      setClubBaseAirfieldId('');
      setAirfieldDraft(emptyAirfieldDraft);
      return;
    }

    setClubBaseAirfieldId(String(data.id));
    setAirfieldDraft({
      name: data.name || '',
      icao: data.icao || '',
      weatherIcao: data.weather_icao || '',
      weatherSourceName: data.weather_source_name || '',
      notes: data.notes || '',
    });
  }, [organisationId]);

  useEffect(() => {
  fetchClubBaseAirfield();
}, [fetchClubBaseAirfield]);

  useEffect(() => {
    if (!organisationId) return;

    setAppSettings(
      loadAppSettings(organisationId)
    );
  }, [organisationId]);

  useEffect(() => {
    let cancelled = false;

    async function resolveRole() {
      if (!user?.id) {
        if (!cancelled) {
          setResolvedRole('');
          setRoleLoading(false);
        }
        return;
      }

      const { data } = await supabase
        .from('users')
        .select('role')
        .eq('auth_id', user.id)
        .maybeSingle();

      if (!cancelled) {
        const nextRole = normalizeRole(
          data?.role,
          user?.user_metadata?.role,
          user?.app_metadata?.role,
          user?.role
        );
        setResolvedRole(nextRole);
        setRoleLoading(false);
      }
    }

    setRoleLoading(true);
    resolveRole();

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.user_metadata?.role, user?.app_metadata?.role, user?.role]);

  const roleCandidates = collectRoles(
    resolvedRole,
    user?.user_metadata?.role,
    user?.app_metadata?.role,
    user?.role
  );

  const isAdmin = roleCandidates.includes('admin') || roleCandidates.includes('owner') || isOwnerByEmail(user?.email);
  const isOwner = roleCandidates.includes('owner') || isOwnerByEmail(user?.email);
  const aircraftAdmin = useAircraftAdmin({
  organisationId,
  user,
  isAdmin,
});

  useEffect(() => {
    let cancelled = false;

    async function loadBillingSettingsFromServer() {
      if (!isAdmin) return;

      const authToken = session?.access_token;
      if (!authToken) return;

      try {
        const response = await fetch('/.netlify/functions/billing-settings-get', {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (!response.ok) return;

        const payload = await response.json();
        if (cancelled) return;

        setAppSettings((prev) => ({
          ...prev,
          payment: {
            ...prev.payment,
            billingBasis: payload?.billingBasis || prev.payment.billingBasis,
            hourlyRate: Number.isFinite(Number(payload?.hourlyRate)) ? Number(payload.hourlyRate) : prev.payment.hourlyRate,
            landingFeeEnabled: typeof payload?.landingFeeEnabled === 'boolean' ? payload.landingFeeEnabled : prev.payment.landingFeeEnabled,
            landingFee: Number.isFinite(Number(payload?.landingFee)) ? Number(payload.landingFee) : prev.payment.landingFee,
          },
        }));

        const normalizedInvoiceSettings =
          normalizeInvoiceSettings(
            payload?.invoiceSettings ||
              defaultInvoiceSettings
          );

        setInvoiceSettings(normalizedInvoiceSettings);
        setInvoiceSavedSettings(
          normalizedInvoiceSettings
        );
      } catch (_err) {
      }
    }

    loadBillingSettingsFromServer();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, apiBase, session?.access_token]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    const connectResult = String(url.searchParams.get('stripe_connect') || '').trim().toLowerCase();
    if (!connectResult) return;

    const accountId = String(url.searchParams.get('account_id') || '').trim();
    const chargesEnabled = ['true', '1', 'yes'].includes(String(url.searchParams.get('charges_enabled') || '').trim().toLowerCase());
    const payoutsEnabled = ['true', '1', 'yes'].includes(String(url.searchParams.get('payouts_enabled') || '').trim().toLowerCase());
    const onboardingComplete = connectResult === 'success' || connectResult === 'return';

    const statusMap = {
      success: 'connected',
      return: 'connected',
      refresh: 'incomplete',
      error: 'error',
      cancel: 'cancelled',
    };

    setAppSettings((prev) => {
      const nextStripeConnect = {
        ...(prev?.payment?.stripeConnect || {}),
        ...(accountId ? { accountId } : {}),
        onboardingComplete,
        chargesEnabled,
        payoutsEnabled,
        status: statusMap[connectResult] || connectResult,
        lastCheckedAt: new Date().toISOString(),
      };

      const nextSettings = {
        ...prev,
        payment: {
          ...prev.payment,
          stripeConnect: nextStripeConnect,
        },
      };

      return saveAppSettings(nextSettings, organisationId);
    });

    if (connectResult === 'success' || connectResult === 'return') {
      setSetupStatus('Stripe Connect is linked for this club.');
    } else if (connectResult === 'refresh') {
      setSetupStatus('Stripe onboarding is not complete yet. Continue onboarding to accept payments.');
    } else {
      setSetupStatus('Stripe onboarding was not completed.');
    }

    url.searchParams.delete('stripe_connect');
    url.searchParams.delete('account_id');
    url.searchParams.delete('charges_enabled');
    url.searchParams.delete('payouts_enabled');
    window.history.replaceState({}, document.title, url.toString());
  }, [organisationId]);

  
  

  

  if (roleLoading) {
    return <div style={{ maxWidth: 700, margin: '3rem auto', color: '#b6eaff' }}>Checking admin access...</div>;
  }

  if (!isAdmin) {
  return <Navigate to="/dashboard" replace />;
}

  const isPaymentsView = isAdminPaymentsView(searchParams);

  if (isPaymentsView) {
    return (
      <AdminPaymentsFeatureGate
        user={user}
        organisationId={organisationId}
        accessToken={session?.access_token}
        onBack={navigate}
      />
    );
  }
  if (isStudentTrainingView(searchParams)) {
    return <StudentTrainingManagement />;
  }
function updateAirfieldDraft(field, value) {
  const uppercaseFields = ['icao', 'weatherIcao'];

  setAirfieldDraft((prev) => ({
    ...prev,
    [field]: uppercaseFields.includes(field)
      ? String(value || '').toUpperCase()
      : value,
  }));
}

async function handleSaveClubBaseAirfield(event) {
  event.preventDefault();

  setAirfieldStatus('');
  setAirfieldError('');

  if (!organisationId) {
    setAirfieldError('Could not identify your organisation.');
    return;
  }

  const payload = {
    name: String(airfieldDraft.name || '').trim(),
    icao: String(airfieldDraft.icao || '').trim().toUpperCase(),
    weather_icao:
      String(airfieldDraft.weatherIcao || '')
        .trim()
        .toUpperCase() || null,
    weather_source_name:
      String(airfieldDraft.weatherSourceName || '').trim() || null,
    notes: String(airfieldDraft.notes || '').trim() || null,
    organisation_id: organisationId,
    singleton: true,
  };

  if (!payload.name || !payload.icao) {
    setAirfieldError(
      'Please enter at least an airfield name and ICAO.'
    );
    return;
  }

  let result;

  if (clubBaseAirfieldId) {
    result = await supabase
      .from('airfields')
      .update(payload)
      .eq('id', clubBaseAirfieldId)
      .eq('organisation_id', organisationId);
  } else {
    result = await supabase
      .from('airfields')
      .upsert([payload], { onConflict: 'organisation_id' })
      .select('id')
      .maybeSingle();
  }

  if (result.error) {
    setAirfieldError('Could not save club base airfield.');
    return;
  }

  if (!clubBaseAirfieldId && result.data?.id) {
    setClubBaseAirfieldId(String(result.data.id));
  }

  setAirfieldStatus('Club base airfield saved.');
  fetchClubBaseAirfield();
}
function updateSetting(path, value) {
  if (
    path[0] === 'operational' &&
    path[1] === 'baseAirfield' &&
    !isOwner
  ) {
    setSetupStatus('Only the owner can change the base airfield.');
    return;
  }

  setSetupStatus('');

  setAppSettings((prev) => ({
    ...prev,
    [path[0]]: {
      ...prev[path[0]],
      [path[1]]: value,
    },
  }));
}

function updateInvoiceSetting(field, value) {
  setInvoiceSettingsStatus('');

  setInvoiceSettings((previous) => ({
    ...previous,
    [field]: value,
  }));
}

function handleResetInvoiceSettings() {
  setInvoiceSettings(invoiceSavedSettings);
  setInvoiceSettingsStatus(
    'Invoice settings reset.'
  );
}

async function handleSaveInvoiceSettings() {
  setInvoiceSettingsStatus('');

  let authToken = '';

  try {
    const {
      data: { session: latestSession },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError) {
      throw sessionError;
    }

    authToken = String(latestSession?.access_token || '').trim();
  } catch (error) {
    console.error('[Invoice Settings save auth session lookup failed]', {
      message: String(error?.message || 'Unknown error'),
    });

    setInvoiceSettingsStatus(
      'Sign in again to save invoice settings.'
    );
    return;
  }

  if (!authToken) {
    console.error('[Invoice Settings save auth header debug]', {
      authorizationHeaderPresent: false,
      tokenLength: 0,
    });

    setInvoiceSettingsStatus(
      'Sign in again to save invoice settings.'
    );
    return;
  }

  const normalizedInvoiceSettings =
    normalizeInvoiceSettings(invoiceSettings);

  try {
    console.error('[Invoice Settings save auth header debug]', {
      authorizationHeaderPresent: true,
      tokenLength: authToken.length,
    });

    const billingResponse = await fetch(
      '/.netlify/functions/billing-settings-save',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          billingBasis: appSettings?.payment?.billingBasis,
          hourlyRate: appSettings?.payment?.hourlyRate,
          landingFeeEnabled:
            appSettings?.payment?.landingFeeEnabled,
          landingFee: appSettings?.payment?.landingFee,
          invoiceSettings: normalizedInvoiceSettings,
        }),
      }
    );

    if (!billingResponse.ok) {
      let apiMessage = 'Could not save invoice settings.';
      let errorPayload = null;
      let responseText = '';

      try {
        errorPayload = await billingResponse.json();
        if (
          errorPayload?.error &&
          typeof errorPayload.error === 'string'
        ) {
          apiMessage = `Could not save invoice settings: ${errorPayload.error}`;
        }
      } catch (_parseErr) {
        try {
          responseText = await billingResponse.text();
        } catch (_textErr) {
        }

        if (responseText) {
          apiMessage = `Could not save invoice settings: ${responseText}`;
        }
      }

      console.error(
        '[Invoice Settings save failed]',
        {
          status: billingResponse.status,
          statusText: billingResponse.statusText,
          url: '/.netlify/functions/billing-settings-save',
          responseText,
          errorPayload,
        }
      );

      setInvoiceSettingsStatus(apiMessage);
      return;
    }

    setInvoiceSettings(normalizedInvoiceSettings);
    setInvoiceSavedSettings(normalizedInvoiceSettings);
    setInvoiceSettingsStatus(
      'Invoice settings saved.'
    );
  } catch (error) {
    console.error('[Invoice Settings save request failed]', error);
    setInvoiceSettingsStatus(
      `Could not save invoice settings: ${String(error?.message || 'Request failed.')}`
    );
  }
}


async function resizeImage(file, maxSize = 1000, quality = 0.9) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      img.src = reader.result;
    };

    reader.onerror = reject;

    img.onload = () => {
      let { width, height } = img;

      if (width > height && width > maxSize) {
        height *= maxSize / width;
        width = maxSize;
      } else if (height > maxSize) {
        width *= maxSize / height;
        height = maxSize;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Image conversion failed'));
            return;
          }

          resolve(
            new File([blob], file.name.replace(/\.\w+$/, '.webp'), {
              type: 'image/webp',
            })
          );
        },
        'image/webp',
        quality
      );
    };

    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}
  async function handleLogoFileSelected(e) {
  const file = e.target.files?.[0];
  setLogoUploadError('');

  if (!file) return;

  if (!file.type.startsWith('image/')) {
    setLogoUploadError('Please upload an image file.');
    return;
  }

  const maxBytes = 10 * 1024 * 1024;

  if (file.size > maxBytes) {
    setLogoUploadError('Image is too large. Please use an image under 10 MB.');
    return;
  }

  try {
    const resizedFile = await resizeImage(file, 1000, 0.9);
    const previewUrl = URL.createObjectURL(resizedFile);

    setClubLogoFile(resizedFile);
    
    
updateSetting(['branding', 'companyLogoDataUrl'], previewUrl);
    setClubAppearanceStatus('Logo selected and optimised. Click Save Club Appearance.');
  } catch (err) {
    setLogoUploadError('Could not optimise this image. Please try another file.');
  }

  e.target.value = '';
}
function handleClubHeroFilesSelected(e, indexToReplace = 0) {
  const file = e.target.files?.[0];

  if (!file || !file.type.startsWith('image/')) {
    setClubAppearanceStatus('Please select an image file.');
    return;
  }

  setClubHeroFiles((prev) => {
    const next = [...prev];

    while (next.length < 5) {
      next.push(null);
    }

    next[indexToReplace] = file;

    return next;
  });

  setClubAppearanceStatus(`Dashboard image ${indexToReplace + 1} selected.`);
  e.target.value = '';
}

function handleRemoveLogo() {
  updateSetting(['branding', 'companyLogoDataUrl'], '');
  setClubLogoFile(null);
  
  setClubAppearanceStatus('Logo removed. Click Save Club Appearance to apply this change.');
  setLogoUploadError('');
}
  async function handleSaveSettings() {
  try {
    const nextSettings = isOwner
      ? appSettings
      : {
          ...appSettings,
          operational: {
            ...appSettings.operational,
            baseAirfield: loadAppSettings(organisationId).operational.baseAirfield,
          },
        };

    const saved = saveAppSettings(nextSettings, organisationId);
    setAppSettings(saved);

    try {
      await saveClubBranding({
        organisationId,
        clubName:
          saved?.operational?.clubName ||
          saved?.branding?.clubName ||
          saved?.operational?.baseAirfield?.name ||
          '',
        logoFile: clubLogoFile,
        heroFiles: clubHeroFiles,
        existingLogoUrl: '',
        existingHeroImages: [],
      });

      setClubAppearanceStatus('Club appearance saved successfully.');

setClubLogoFile(null);
    } catch (brandingErr) {
  console.error('Branding upload failed:', brandingErr);

  setClubAppearanceStatus(
    `Branding upload failed: ${brandingErr?.message || 'Unknown error'}`
  );
}

    const authToken = session?.access_token;

    if (!authToken) {
      setSetupStatus('Settings saved locally. Sign in again to sync billing settings to server.');
      return;
    }

    const billingResponse = await fetch('/.netlify/functions/billing-settings-save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        billingBasis: saved?.payment?.billingBasis,
        hourlyRate: saved?.payment?.hourlyRate,
        landingFeeEnabled: saved?.payment?.landingFeeEnabled,
        landingFee: saved?.payment?.landingFee,
        invoiceSettings,
      }),
    });

    if (!billingResponse.ok) {
      let message = 'Settings saved locally. Billing sync failed.';

      try {
        const payload = await billingResponse.json();
        message = payload?.error ? `Settings saved locally. Billing sync failed: ${payload.error}` : message;
      } catch (_parseErr) {
      }

      setSetupStatus(message);
      return;
    }

    setSetupStatus('Settings saved and billing synced to server.');
  } catch (err) {
    setSetupStatus('Could not save setup. Please try again.');
  }
}

  function updateStripeConnectSettings(partial) {
    const nextStripeConnect = {
      ...(appSettings?.payment?.stripeConnect || {}),
      ...(partial || {}),
      lastCheckedAt: new Date().toISOString(),
    };

    const nextSettings = {
      ...appSettings,
      payment: {
        ...appSettings.payment,
        stripeConnect: nextStripeConnect,
      },
    };

    const saved = saveAppSettings(nextSettings, organisationId);
    setAppSettings(saved);
  }

  async function handleStartStripeConnect() {
    if (!isOwner) {
      setSetupStatus('Only the owner can link Stripe Connect.');
      return;
    }

    setStripeConnectLoading(true);
    setSetupStatus('');

    try {
      const authToken = session?.access_token;

      if (!authToken) {
        throw new Error('Session expired. Please sign in again.');
      }

      const response = await fetch(`${apiBase}/api/billing/stripe/connect/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          mode: appSettings?.payment?.stripeConnect?.mode || 'standard',
          ownerUserId: user?.id || undefined,
          ownerEmail: user?.email || undefined,
          orgName: user?.user_metadata?.org_name || user?.app_metadata?.org_name || '',
          returnUrl: `${window.location.origin}/admin?stripe_connect=return`,
          refreshUrl: `${window.location.origin}/admin?stripe_connect=refresh`,
        }),
      });

      let payload = {};

      try {
        payload = await response.json();
      } catch (parseErr) {
        payload = {};
      }

      if (!response.ok) {
        throw new Error(payload?.error || payload?.detail || 'Could not start Stripe Connect onboarding.');
      }

      if (payload?.accountId) {
        updateStripeConnectSettings({
          accountId: String(payload.accountId),
          status: 'pending',
        });
      }

      if (!payload?.url) {
        throw new Error('Stripe Connect onboarding URL was not returned by server.');
      }

      window.location.assign(payload.url);
    } catch (err) {
      setSetupStatus(err?.message || 'Could not start Stripe Connect onboarding.');
      setStripeConnectLoading(false);
    }
  }

  async function handleRefreshStripeConnectStatus() {
    const accountId = String(appSettings?.payment?.stripeConnect?.accountId || '').trim();

    if (!accountId) {
      setSetupStatus('No Stripe account ID is stored yet. Start onboarding first.');
      return;
    }

    setStripeConnectLoading(true);
    setSetupStatus('');

    try {
      const authToken = session?.access_token;

      if (!authToken) {
        throw new Error('Session expired. Please sign in again.');
      }

      const response = await fetch(`${apiBase}/api/billing/stripe/connect/status?accountId=${encodeURIComponent(accountId)}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload?.error || payload?.detail || 'Could not refresh Stripe Connect status.');
      }

      updateStripeConnectSettings({
        accountId,
        onboardingComplete: !!payload?.onboardingComplete,
        chargesEnabled: !!payload?.chargesEnabled,
        payoutsEnabled: !!payload?.payoutsEnabled,
        status: String(payload?.status || '').trim().toLowerCase(),
      });

      setSetupStatus('Stripe Connect status refreshed.');
    } catch (err) {
      setSetupStatus(err?.message || 'Could not refresh Stripe Connect status.');
    } finally {
      setStripeConnectLoading(false);
    }
  }

  

  

  return (
    <div style={{ maxWidth: 700, margin: '3rem auto', background: '#181a2a', borderRadius: 16, padding: '2.5rem 2rem', color: 'white', boxShadow: '0 4px 32px rgba(0,0,0,0.18)' }}>
      <h2 style={{ color: '#1ecfff', fontWeight: 700, fontSize: '2rem', marginBottom: '1.5rem' }}>ADMIN CONTROL PANEL</h2>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: '1.3rem' }}>
  <button
    type="button"
    onClick={() => navigate('/dashboard')}
    style={{
      background: '#b6eaff',
      color: '#181a2a',
      border: 'none',
      borderRadius: 8,
      padding: '0.6rem 1rem',
      fontWeight: 700,
      cursor: 'pointer',
    }}
  >
    HOME
  </button>

  <button
    type="button"
    onClick={() => navigate('/Members')}
    style={{
      background: '#2dd3be',
      color: '#081018',
      border: 'none',
      borderRadius: 8,
      padding: '0.6rem 1rem',
      fontWeight: 700,
      cursor: 'pointer',
    }}
  >
    MEMBERS
  </button>
  <button
    type="button"
    onClick={() => navigate('/admin?section=operations&view=student-training')}
    style={{
      background: '#1ecfff',
      color: '#081018',
      border: 'none',
      borderRadius: 8,
      padding: '0.6rem 1rem',
      fontWeight: 700,
      cursor: 'pointer',
    }}
  >
    STUDENT TRAINING
  </button>
</div>

      

  
      

      <AircraftFleetPanel
  aircraftList={aircraftAdmin.aircraftList}
  aircraftDefects={aircraftAdmin.aircraftDefects}
  appSettings={appSettings}
  regInput={aircraftAdmin.regInput}
  
  success={aircraftAdmin.success}
  error={aircraftAdmin.error}
  aircraftStatusMessage={aircraftAdmin.aircraftStatusMessage}
  aircraftStatusError={aircraftAdmin.aircraftStatusError}
  expandedStatusAircraftId={aircraftAdmin.expandedStatusAircraftId}
  aircraftStatusActionId={aircraftAdmin.aircraftStatusActionId}
  expandedWbAircraftId={aircraftAdmin.expandedWbAircraftId}
  wbDraftMap={aircraftAdmin.wbDraftMap}
  aircraftDetailsDraftMap={aircraftAdmin.aircraftDetailsDraftMap}
  onRegInputChange={aircraftAdmin.handleRegInputChange}
  onAddAircraft={aircraftAdmin.handleAddAircraft}
  onUpdateAircraftMethod={aircraftAdmin.handleUpdateAircraftMethod}
  onAircraftImageUpload={aircraftAdmin.handleAircraftImageUpload}
  onToggleWb={aircraftAdmin.toggleWbEditor}
  onToggleReview={aircraftAdmin.setExpandedStatusAircraftId}
  onRemoveAircraft={aircraftAdmin.handleRemoveAircraft}
  onAircraftStatusChange={aircraftAdmin.handleAircraftStatusChange}
  onRectifyDefect={aircraftAdmin.handleRectifyDefect}
  onUpdateWbDraft={aircraftAdmin.updateWbDraft}
  onUpdateAircraftDetailsDraft={
    aircraftAdmin.updateAircraftDetailsDraft
  }
  onSaveAircraftDetails={
    aircraftAdmin.handleSaveAircraftDetails
  }
  onSaveWbConstants={aircraftAdmin.handleSaveWbConstants}
/>
      <DocumentsPanel
  documents={documents}
  documentsStatus={documentsStatus}
  onDocumentUpload={handleDocumentUpload}
  onPrintDocument={handlePrintDocument}
  onDeleteDocument={handleDeleteDocument}
  formatDate={formatWingitDate}
/>
<ClubAppearancePanel
  organisationId={organisationId}
  appSettings={appSettings}
  logoUploadError={logoUploadError}
  clubHeroFiles={clubHeroFiles}
  clubAppearanceStatus={clubAppearanceStatus}
  handleLogoFileSelected={handleLogoFileSelected}
  handleRemoveLogo={handleRemoveLogo}
  handleClubHeroFilesSelected={handleClubHeroFilesSelected}
  handleSaveClubAppearance={handleSaveSettings}
/>

 
      
      <AnnouncementsPanel
        organisationId={organisationId}
        user={user}
      />
      <ClubSettingsPanel
  clubSettingsOpen={clubSettingsOpen}
  onToggleClubSettings={() =>
    setClubSettingsOpen((previous) => !previous)
  }
  airfieldDraft={airfieldDraft}
  airfieldStatus={airfieldStatus}
  airfieldError={airfieldError}
  onUpdateAirfieldDraft={updateAirfieldDraft}
  onSaveClubBaseAirfield={handleSaveClubBaseAirfield}
  appSettings={appSettings}
  onUpdateSetting={updateSetting}
  isOwner={isOwner}
  stripeConnectLoading={stripeConnectLoading}
  onStartStripeConnect={handleStartStripeConnect}
  onRefreshStripeConnectStatus={
    handleRefreshStripeConnectStatus
  }
  invoiceSettings={invoiceSettings}
  invoiceSettingsStatus={invoiceSettingsStatus}
  onInvoiceSettingChange={updateInvoiceSetting}
  onSaveInvoiceSettings={handleSaveInvoiceSettings}
  onResetInvoiceSettings={handleResetInvoiceSettings}
  onSaveSettings={handleSaveSettings}
  setupStatus={setupStatus}
/>
    </div>
  );
}








 


  

  